import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";

interface TouchDetectionOptions {
  threshold: number; // Number of touches within timeframe to consider abnormal
  timeframe: number; // Timeframe in milliseconds
  sampleSize: number; // Number of occurrences before reporting
  reportInterval: number; // Minimum time between reports in milliseconds
}

const defaultOptions: TouchDetectionOptions = {
  threshold: 15, // 15 touches
  timeframe: 1000, // within 1 second
  sampleSize: 3, // Need to see 3 occurrences
  reportInterval: 180000, // Report at most once per 3 minutes
};

/**
 * Hook to detect abnormal touch behaviors (like static causing rapid clicks)
 * and report them to Sentry
 */
export function useTouchDetection(options?: Partial<TouchDetectionOptions>) {
  const { t } = useTranslation();
  const opts = { ...defaultOptions, ...options };
  const touchTimesRef = useRef<number[]>([]);
  const abnormalCountRef = useRef<number>(0);
  const lastReportTimeRef = useRef<number>(0);

  useEffect(() => {
    const handleTouch = () => {
      const now = Date.now();
      touchTimesRef.current.push(now);
      // Keep only touches within the timeframe
      touchTimesRef.current = touchTimesRef.current.filter((time) => now - time < opts.timeframe);

      // Check if we have abnormal activity
      if (touchTimesRef.current.length > opts.threshold) {
        abnormalCountRef.current += 1;

        // If we've seen enough samples and enough time has passed since last report
        if (
          abnormalCountRef.current >= opts.sampleSize &&
          now - lastReportTimeRef.current > opts.reportInterval
        ) {
          const abnormalBehaviorMessage = t(
            "touchDetection.abnormalBehavior",
            "Abnormal touch behavior detected"
          );
          const touchscreenIssueMessage = t(
            "touchDetection.touchscreenIssue",
            "Potential touchscreen hardware issue"
          );

          const lockerId = window.localStorage.getItem("lockerId") || "unknown";

          Sentry.withScope((scope) => {
            scope.setLevel("warning");
            scope.setTags({
              issue_type: "hardware",
              component: "touchscreen",
              touches_detected: touchTimesRef.current.length.toString(),
              timeframe_ms: opts.timeframe.toString(),
              device_id: lockerId,
            });
            Sentry.captureMessage(abnormalBehaviorMessage, "warning");
          });

          // Reset counts and update last report time
          abnormalCountRef.current = 0;
          lastReportTimeRef.current = now;
          console.warn(
            `${abnormalBehaviorMessage}: ${touchTimesRef.current.length} touches in ${opts.timeframe}ms. ${touchscreenIssueMessage}`
          );
        }
      }
    };

    // Attach event listeners
    document.addEventListener("touchstart", handleTouch);
    if (process.env.NODE_ENV === "development") {
      document.addEventListener("mousedown", handleTouch);
    }

    // Cleanup on unmount
    return () => {
      // Clean up event listeners
      document.removeEventListener("touchstart", handleTouch);
      if (process.env.NODE_ENV === "development") {
        document.removeEventListener("mousedown", handleTouch);
      }
    };
  }, [opts.threshold, opts.timeframe, opts.sampleSize, opts.reportInterval, t]);
}
